import { useEffect, useRef, useState } from "react";
import {
  Download,
  FileDown,
  FileText,
  Loader2,
  Table as TableIcon,
} from "lucide-react";
import { useAuth } from "@/modules/auth/hooks/useAuth";

const API_BASE = (
  import.meta.env.VITE_API_URL ?? 
  (import.meta.env.DEV ? "http://localhost:3000/api" : "/api")
).replace(/\/$/, "");

const BORDER = "hsl(210,30%,85%)";
const ICON_COLOR = "hsl(215,16%,47%)";

export type ExportFormat = "csv" | "excel" | "pdf";

export interface ExportButtonProps {
  module: string;
  filters?: Record<string, unknown>;
  columns?: string[];
  visibleColumns?: string[];
  orientation?: "portrait" | "landscape";
  disabled?: boolean;
  className?: string;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match ? match[1] : null;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportButton({
  module,
  filters,
  columns,
  visibleColumns,
  orientation,
  disabled,
  className,
}: ExportButtonProps) {
  const { accessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function doExport(format: ExportFormat) {
    setOpen(false);
    setError(null);
    setLoading(format);
    try {
      const cols = visibleColumns ?? columns;
      const res = await fetch(`${API_BASE}/${module}/export`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {}),
        },
        body: JSON.stringify({
          format,
          filters: filters ?? {},
          columns: cols,
          orientation,
        }),
      });

      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const json = await res.json();
          if (json && typeof json.error === "string") msg = json.error;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const filename =
        filenameFromDisposition(res.headers.get("Content-Disposition")) ??
        `${module}-${new Date().toISOString().slice(0, 10)}.${
          format === "excel" ? "xlsx" : format
        }`;
      triggerDownload(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
      setTimeout(() => setError(null), 4000);
    } finally {
      setLoading(null);
    }
  }

  const isLoading = loading !== null;

  return (
    <div
      className={"relative inline-flex" + (className ? ` ${className}` : "")}
      ref={containerRef}
    >
      <button
        type="button"
        disabled={disabled || isLoading}
        onClick={() => setOpen((v) => !v)}
        title="Export"
        aria-label="Export"
        aria-expanded={open}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white transition-colors hover:bg-[hsl(210,30%,97%)] disabled:cursor-not-allowed disabled:opacity-60"
        style={{ borderColor: BORDER, color: ICON_COLOR }}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        ) : (
          <Download className="h-4 w-4" strokeWidth={1.5} />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 overflow-hidden rounded-lg border bg-white"
          style={{
            top: "calc(100% + 4px)",
            borderColor: BORDER,
            minWidth: 160,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <button
            type="button"
            onClick={() => doExport("csv")}
            className="flex w-full items-center gap-2 bg-transparent text-left text-[13px] hover:bg-[hsl(210,30%,97%)]"
            style={{ height: 34, padding: "0 14px" }}
          >
            <FileText
              className="h-3.5 w-3.5"
              strokeWidth={1.5}
              style={{ color: "hsl(215,16%,47%)" }}
            />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => doExport("excel")}
            className="flex w-full items-center gap-2 bg-transparent text-left text-[13px] hover:bg-[hsl(210,30%,97%)]"
            style={{ height: 34, padding: "0 14px" }}
          >
            <TableIcon
              className="h-3.5 w-3.5"
              strokeWidth={1.5}
              style={{ color: "hsl(142,72%,36%)" }}
            />
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => doExport("pdf")}
            className="flex w-full items-center gap-2 bg-transparent text-left text-[13px] hover:bg-[hsl(210,30%,97%)]"
            style={{ height: 34, padding: "0 14px" }}
          >
            <FileDown
              className="h-3.5 w-3.5"
              strokeWidth={1.5}
              style={{ color: "hsl(0,72%,46%)" }}
            />
            Export PDF
          </button>
        </div>
      )}

      {error && (
        <div
          className="absolute right-0 z-[60] w-64 rounded-md border px-3 py-2 text-xs shadow-md"
          style={{
            top: "calc(100% + 4px)",
            borderColor: "hsl(0,70%,80%)",
            background: "hsl(0,70%,96%)",
            color: "hsl(0,70%,35%)",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
